/**
 * Role queries and the one mutation that changes a matrix.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@/api/client';
import type { PermissionMatrix, RoleRead } from '@/api/schema';
import { CURRENT_USER_KEY } from '@/features/auth/api';

export const ROLES_KEY = ['roles'] as const;

export function useRoleList() {
  return useQuery({
    queryKey: ROLES_KEY,
    queryFn: () => api.get<RoleRead[]>('/api/v1/roles'),
  });
}

export function useUpdateRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      permissions,
      description,
    }: {
      id: string;
      permissions?: PermissionMatrix;
      description?: string;
    }) => api.patch<RoleRead>(`/api/v1/roles/${id}`, { permissions, description }),

    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ROLES_KEY });
      // The signed-in user's own matrix may have just changed; an admin editing the
      // admin role is editing their own access. Refetching it makes the navigation
      // reflect the change immediately rather than at the next sign-in.
      await queryClient.invalidateQueries({ queryKey: CURRENT_USER_KEY });
    },
  });
}
